import type { Messages } from "./en";

/**
 * French (Canada) message dictionary. Mirrors the English shape in en.ts.
 */
export const fr = {
  nav: {
    apps: "Applications",
    webSupport: "Soutien web",
    hosting: "Hébergement",
    help: "Aide",
    helpCenter: "Centre d'aide",
    faqs: "FAQ",
    clients: "Clients",
    contact: "Contact",
    support: "Soutien",
    navigation: "Navigation",
    closeMenu: "Fermer le menu",
    homeAria: "Accueil CloudAxe",
    hipaaForms: "Formulaires conformes HIPAA",
    overview: "Aperçu",
    getStarted: "Commencer",
    pricing: "Tarifs",
    developers: "Développeurs",
    api: "API",
    wordpress: "WordPress",
    joomla: "Joomla",
    faq: "FAQ",
    security: "Sécurité",
    customApps: "Applications sur mesure",
    proudlyCanadian: "Fièrement canadien",
    langEn: "EN",
    langFr: "FR",
    switchToEnglish: "Switch to English",
    switchToFrench: "Passer au français",
  },
  footer: {
    tagline:
      "Applications, soutien web et hébergement pour les petites entreprises. Diagnostic clair, correction nette, prévention durable.",
    services: "Services",
    help: "Aide",
    clients: "Clients",
    clientPortal: "Portail client",
    apps: "Applications",
    webSupport: "Soutien web",
    hosting: "Hébergement",
    helpCenter: "Centre d'aide",
    faq: "FAQ",
    contact: "Contact",
    privacy: "Confidentialité",
    terms: "Conditions",
    copyright: "CloudAxe — Break Thru.",
    breakThru: "Break Thru",
  },
  banner: {
    axcIsNow: "AXC est maintenant",
  },
  privacy: {
    aria: "Consentement à la confidentialité",
    body:
      "Nous utilisons des témoins et des technologies similaires pour faire fonctionner ce site et comprendre son utilisation. Vous pouvez accepter ou refuser l'utilisation non essentielle. Consultez notre",
    policy: "Politique de confidentialité",
    decline: "Refuser",
    accept: "Accepter",
  },
  contactForm: {
    title: "Envoyer un message",
    subtitle: "Nous répondons généralement en un jour ouvrable.",
    success: "Merci — votre message a été envoyé. Nous vous répondrons bientôt.",
    name: "Nom",
    email: "Courriel",
    message: "De quoi avez-vous besoin ?",
    send: "Envoyer le message",
    sending: "Envoi…",
    errorFallback: "Impossible d'envoyer votre message. Veuillez réessayer.",
  },
  home: {
    subtitle: "Break Thru",
    eyebrow: "Soutien web expert",
    uNext: "U Next?",
    heroLine1: "Traversez le bruit —",
    heroLine2Before: "Soutien web",
    heroLine2Mid: "pour les petites entreprises qui",
    heroLine2Accent: "livrent.",
    ctaSupport: "Obtenir du soutien technique",
    ctaHipaa: "Obtenir des formulaires HIPAA",
    ctaHosting: "Obtenir de l'hébergement",
    servicesEyebrow: "Ce que nous faisons",
    servicesTitle: "Que fait CloudAxe pour les petites entreprises ?",
    servicesIntro:
      "CloudAxe offre trois services aux petites entreprises : des applications sur mesure (formulaires conformes HIPAA, outils d'événements et applications personnalisées), un soutien web expert pour WordPress, Next.js et Joomla, et un hébergement géré avec sauvegardes, surveillance et environnements de préproduction. Commencez là où ça compte le plus.",
    getQuote: "Demander un devis",
    serviceAppsTitle: "Applications",
    serviceAppsDesc:
      "Des applications conçues pour simplifier le travail de votre petite entreprise — formulaires, événements et outils sur mesure qui correspondent vraiment à vos flux.",
    serviceAppsBullets: [
      "Formulaires conformes HIPAA",
      "Outils d'événements et d'inscription",
      "Applications sur mesure pour votre stack",
    ],
    serviceSupportTitle: "Soutien web",
    serviceSupportDesc:
      "Plus de 20 ans d'expérience spécialisée en WordPress, Next.js et Joomla. Dépannage, mises à jour, sécurité et performance — quand vous en avez besoin.",
    serviceSupportBullets: [
      "WordPress, Next.js, Joomla",
      "Dépannage et correctifs",
      "Sécurité et performance",
    ],
    serviceHostingTitle: "Hébergement",
    serviceHostingDesc:
      "Hébergement géré qui priorise la disponibilité, la vitesse et une communication claire. Une stack propre, surveillée 24/7, avec des sauvegardes que vous pouvez vraiment restaurer.",
    serviceHostingBullets: [
      "Sauvegardes + tests de restauration",
      "Surveillance + alertes",
      "Environnements de préproduction",
    ],
    providersEyebrow: "Intégrations de confiance",
    providersTitle: "Fournisseurs",
    providersIntro: "Nous nous intégrons aux plateformes que vous utilisez déjà.",
    outcomesEyebrow: "Résultats",
    outcomesTitle: "Résultats Break Thru",
    outcomesIntro:
      "Diagnostic clair, correction nette, puis prévention. C'est tout l'objectif.",
    outcome1t: "Incidents traités",
    outcome1d: "Triage rapide, communication claire, correction de la cause.",
    outcome2t: "Sites plus rapides",
    outcome2d: "Mise en cache, stratégie CDN, réglage BD et optimisation d'images.",
    outcome3t: "Sécurité renforcée",
    outcome3d: "Mises à jour, configurations durcies, nettoyage de maliciel, surveillance.",
    techEyebrow: "Stacks modernes",
    techTitle: "Technologies",
    techIntro: "Nous construisons et soutenons des stacks modernes sur lesquelles vous pouvez compter.",
    ctaEyebrow: "Prêts quand vous l'êtes",
    ctaTitle: "Prêt à Break Thru ?",
    ctaBody:
      "Parlez-nous de votre site, de votre idée d'application ou du problème qui vous bloque. Nous ferons le triage ensemble.",
    ctaCall: "Réserver un appel rapide",
    ctaHelp: "Visiter le centre d'aide",
    metaTitle: "Soutien web, applications et hébergement pour PME",
    metaDescription:
      "CloudAxe aide les petites entreprises à avancer : soutien web expert pour WordPress, Next.js et Joomla, applications sur mesure comme les formulaires HIPAA, et hébergement géré avec des sauvegardes que vous pouvez restaurer.",
  },
  webSupport: {
    subtitle: "Soutien web",
    eyebrow: "Soutien web expert",
    uNext: "U Next?",
    outcomesEyebrow: "Résultats",
    outcomesTitle: "Que couvre le soutien web CloudAxe ?",
    outcomesIntro:
      "Le soutien web CloudAxe couvre les mises à jour, le dépannage, le durcissement de la sécurité et l'optimisation des performances pour les sites WordPress, Next.js et Joomla — ainsi que les problèmes DNS, SSL et courriel lorsqu'ils affectent votre site. Interventions ponctuelles et forfaits récurrents sont disponibles.",
    outcome1t: "Incidents traités",
    outcome1d: "Triage rapide, communication claire, correction de la cause.",
    outcome2t: "Sites plus rapides",
    outcome2d: "Mise en cache, stratégie CDN, réglage BD et optimisation d'images.",
    outcome3t: "Sécurité renforcée",
    outcome3d: "Mises à jour, configurations durcies, nettoyage de maliciel, surveillance.",
    metaTitle: "Soutien web pour WordPress, Next.js et Joomla",
    metaDescription:
      "Soumettez une demande de soutien web en quelques secondes. WordPress, Next.js, Joomla et plus. Plus de 20 ans d'expérience — commencez avec notre formulaire rapide.",
  },
  hosting: {
    subtitle: "Hébergement",
    eyebrow: "VPS et hébergement mutualisé",
    title: "Hébergement pour PME",
    introBefore: "L'hébergement géré CloudAxe commence à",
    introMid: "pour les plans mutualisés et",
    introAfter:
      "pour les VPS. Choisissez des ressources VPS dédiées pour un contrôle total et de la marge de croissance, ou l'hébergement mutualisé pour une stack gérée et économique. Les deux incluent sauvegardes avec tests de restauration, surveillance de disponibilité et soutien.",
    highlightBackups: "Sauvegardes",
    highlightBackupsD: "Automatisées + restauration testée",
    highlightSsl: "SSL",
    highlightSslD: "Renouvellement auto et alertes d'expiration",
    highlightSpeed: "Vitesse",
    highlightSpeedD: "Mise en cache + options CDN",
    highlightSecurity: "Sécurité",
    highlightSecurityD: "Durcissement + surveillance",
    vpsWhatTitle: "Qu'est-ce qu'un serveur VPS ?",
    vpsWhatBody:
      "Un serveur privé virtuel (VPS) est une portion d'une machine physique avec du CPU et de la RAM qui vous sont dédiés. Vous obtenez un accès root et des performances prévisibles sans partager les ressources avec d'autres locataires comme en hébergement mutualisé. Comparé au bare metal, un VPS est moins cher et plus rapide à provisionner tout en offrant un contrôle complet; comparé à l'hébergement mutualisé, vous obtenez de l'isolation, de meilleures performances sous charge, et la possibilité d'exécuter des logiciels personnalisés et de monter en capacité au besoin.",
    vpsTitle: "Serveurs VPS",
    vpsIntro:
      "CPU et RAM dédiés, accès root et stack gérée. Idéal pour les applications qui ont besoin de performances prévisibles et de marge de croissance.",
    sharedTitle: "Hébergement mutualisé",
    sharedIntro:
      "Hébergement mutualisé géré pour les sites de petites entreprises. Un compte, plusieurs sites sur les paliers supérieurs — SSL, sauvegardes et surveillance inclus.",
    billing: "Facturation",
    monthly: "Mensuel",
    yearly: "Annuel",
    save20: "Économisez 20 %",
    popular: "Populaire",
    billedYearly: "facturé annuellement",
    cadence: "/mois",
    cancelAnytime: "Annulez en tout temps · Sans surprise",
    talkToSales: "Parler aux ventes",
    metaTitle: "Hébergement géré pour PME",
    metaDescription:
      "Serveurs VPS et hébergement mutualisé pour les petites entreprises. Hébergement géré avec sauvegardes, surveillance, préproduction et soutien.",
  },
  pricing: {
    subtitle: "Tarifs",
    badge: "Intégrations IA",
    title: "Tarifs —",
    titleAccent: "fourchettes possibles pour les intégrations IA",
    intro:
      "Nous cadrons chaque mandat selon vos objectifs et votre budget. Les fourchettes ci-dessous sont indicatives; votre prix réel dépend de vos besoins — stratégie, intégrations, formulaires HIPAA, applications sur mesure ou soutien continu.",
    rangesTitle: "Fourchettes de prix possibles",
    rangesIntroBefore: "À partir de",
    rangesIntroAfter:
      "pour un travail plus léger ou par projet, évoluant avec la portée (p. ex. stratégie, intégrations, configuration de formulaires ou soutien continu). Les mandats plus importants ou sur mesure sont cotés après une courte découverte.",
    starterLabel: "Démarrage / ponctuel :",
    starterBody:
      "À partir de {price}/mois ou par projet — p. ex. une intégration unique, une configuration de formulaire ou une courte session stratégique.",
    ongoingLabel: "Soutien continu :",
    ongoingBody:
      "Options de forfait mensuel pour des intégrations IA continues, la gestion de formulaires ou le soutien — tarifé selon la portée.",
    largerLabel: "Mandats plus importants :",
    largerBody:
      "Applications sur mesure, intégrations multi-systèmes ou travaux lourds en conformité HIPAA — nous cotons après découverte.",
    getQuote: "Demander un devis",
    metaTitle: "Tarifs des intégrations IA",
    metaDescription:
      "Tarifs des intégrations IA CloudAxe. Nous cadrons chaque mandat selon vos besoins.",
  },
  help: {
    subtitle: "Centre d'aide",
    eyebrow: "Aide · Applications · Soutien web · Hébergement",
    title: "Comment pouvons-nous aider ?",
    introBefore:
      "Recherchez sur cette page pour trouver rapidement des conseils sur les applications, le soutien web et l'hébergement. Pour tout ce qui est urgent ou inhabituel, vous pouvez toujours",
    contactLink: "nous contacter directement",
    introAfter: ".",
    searchLabel: "Rechercher dans l'aide",
    searchPlaceholder: "Rechercher Applications, Soutien web, Hébergement…",
    filtering: "Filtrage des sections qui mentionnent votre recherche.",
    noMatches: "Aucun résultat dans Applications, Soutien web ou Hébergement.",
    browseEyebrow: "Parcourir par domaine",
    quickHelp: "Aide rapide",
    browseIntro:
      "Accédez au domaine qui vous intéresse le plus. Utilisez la recherche ci-dessus pour affiner ce que vous voyez.",
    sectionAppsTitle: "Applications",
    sectionAppsDesc:
      "Trouvez de l'aide pour les formulaires HIPAA, Event Maker, Rubric et les applications sur mesure adaptées à vos flux.",
    sectionAppsItems: [
      "Démarrer avec les formulaires conformes HIPAA",
      "Configuration Event Maker et inscription aux événements",
      "Configuration et notation Rubric",
      "Intégration d'applications sur mesure et demandes de fonctionnalités",
    ],
    sectionSupportTitle: "Soutien web",
    sectionSupportDesc:
      "Soutien pour WordPress, Next.js et Joomla, plus DNS, SSL, courriel et déploiements.",
    sectionSupportItems: [
      "Dépannage des problèmes WordPress courants",
      "Déploiements Next.js et variables d'environnement",
      "Mises à jour et extensions Joomla",
      "Bases du DNS, SSL et routage du courriel",
    ],
    sectionHostingTitle: "Hébergement",
    sectionHostingDesc:
      "Guides pour l'hébergement géré CloudAxe : performance, sauvegardes et réponse aux incidents.",
    sectionHostingItems: [
      "Comprendre votre plan d'hébergement",
      "Sauvegardes, tests de restauration et préproduction",
      "Optimisation des performances et mise en cache",
      "Que faire lors d'une panne",
    ],
    metaTitle: "Centre d'aide",
    metaDescription:
      "Aide CloudAxe pour les applications, le soutien web et l'hébergement. Cherchez des conseils ou contactez-nous pour les urgences.",
  },
  faqs: {
    subtitle: "FAQ",
    eyebrow: "Aide · FAQ",
    title: "Questions fréquemment posées",
    introBefore:
      "Questions courantes sur les applications, le soutien web et l'hébergement. Si vous ne voyez pas votre question ici, vous pouvez toujours",
    contactLink: "nous contacter directement",
    introAfter: ".",
    metaTitle: "Questions fréquemment posées",
    metaDescription:
      "FAQ sur les applications, le soutien web et l'hébergement CloudAxe pour les petites entreprises.",
  },
  contact: {
    subtitle: "Contact",
    eyebrow: "Nous joindre",
    title: "Contacter CloudAxe",
    intro:
      "Dites-nous ce que vous utilisez et ce dont vous avez besoin — panne, vitesse, sécurité, courriel, déploiements ou autre. Nous répondrons avec un plan et les prochaines étapes.",
    chatTitle: "Clavardage",
    chatBody: "Joignez-nous tout de suite. Cliquez sur ce bouton pour démarrer une conversation.",
    sendTitle: "Envoyer un message",
    sendBody:
      "Nous répondons généralement en un jour ouvrable. Pour l'hébergement et les migrations, le soutien technique et le dépannage, ou les déploiements d'applications et la surveillance — décrivez votre situation et nous vous répondrons.",
    bulletHosting: "Hébergement et migrations",
    bulletSupport: "Soutien technique et dépannage",
    bulletApps: "Déploiements d'applications et surveillance",
    phoneTitle: "Téléphone",
    phoneBody: "Appelez-nous pendant les heures d'ouverture.",
    emailTitle: "Courriel",
    emailBody:
      "Vous préférez le courriel ? Envoyez-nous une note et nous répondrons en un jour ouvrable.",
    emailGeneral: "support@cloudaxe.io — Général et soutien",
    facebookTitle: "Facebook",
    facebookBody: "Suivez-nous pour les mises à jour de service et les annonces.",
    linkedinTitle: "LinkedIn",
    linkedinBody: "Connectez-vous avec nous pour les nouvelles de l'entreprise.",
    whatsappTitle: "WhatsApp",
    whatsappBody: "Touchez pour démarrer une conversation WhatsApp avec nous.",
    whatsappLink: "Clavarder avec nous sur WhatsApp",
    metaTitle: "Nous contacter",
    metaDescription:
      "Contactez CloudAxe pour les applications, le soutien web et l'hébergement. Dites-nous ce que vous utilisez et ce dont vous avez besoin — nous répondrons avec un plan.",
  },
  intake: {
    submit: "Soumettre la demande",
    submitting: "Envoi…",
  },
} satisfies Messages;
